// composables/useSessionAuth.js
import { ref } from 'vue'
import { clearAuthMeCache, fetchAuthMe } from '@/services/authMeCache'

// Global reactive state for user data
const userData = ref(null)
const userAbilities = ref([])
const stores = ref([])
const selectedStore = ref(null)
const isLoading = ref(false)

export function useSessionAuth() {
  
  /**
   * Fetch user data from session
   */
  const fetchUserData = async () => {
    try {
      isLoading.value = true

      const response = await fetchAuthMe({ timeout: 10000 })

      if (response.status === 200 && response.data) {
        const { userData: user, userAbilities: abilities, stores: userStores, selectedStore: selected } = response.data

        // Update reactive state (include avatar if provided)
        userData.value = {
          id: user.id,
          email: user.email,
          firstname: user.firstname,
          lastname: user.lastname,
          username: user.username,
          phone: user.phone,
          avatar: user.avatar || null,
          role: {
            id: user.role_id,
            name: user.role?.name || (Number(user.role_id || 0) === 2 ? 'admin' : 'user'),
          },
          StoreID: user.StoreID,
          StoreName: user.StoreName,
          StoreSelected: user.StoreSelected,
          TabernaAccountID: user.TabernaAccountID,
        }
        
        // Persist to localStorage for components still reading from it
        try {
          localStorage.setItem('userData', JSON.stringify(userData.value))
        } catch (e) {
          console.warn('Failed to persist userData to localStorage:', e)
        }

        // Dispatch event so existing listeners (UserProfile) update immediately
        window.dispatchEvent(new CustomEvent('user-profile-updated'))
        
        userAbilities.value = abilities || []
        stores.value = userStores || []
        selectedStore.value = selected

        return true
      }
    } catch (error) {
      console.log('Failed to fetch user data:', error.response?.status, error.message)
      
      // If we get a 401, the user is not authenticated
      if (error.response?.status === 401) {
        clearAuth()
        
        return false
      }
      
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = async () => {
    // If we already have user data, consider authenticated
    if (userData.value) {
      return true
    }

    // Otherwise, try to fetch from session
    return await fetchUserData()
  }

  /**
   * Clear authentication data
   */
  const clearAuth = () => {
    userData.value = null
    userAbilities.value = []
    stores.value = []
    selectedStore.value = null
    clearAuthMeCache()

    // Also clear localStorage to prevent stale data
    try {
      localStorage.removeItem('userData')
      localStorage.removeItem('userAbilities')
      localStorage.removeItem('accessToken')

      // Notify components that user data has been cleared
      window.dispatchEvent(new CustomEvent('user-profile-updated'))
    } catch (e) {
      console.warn('Failed to clear localStorage:', e)
    }
  }

  /**
   * Get current user data
   */
  const getUser = () => userData.value

  /**
   * Get user abilities/permissions
   */
  const getAbilities = () => userAbilities.value

  /**
   * Get user stores
   */
  const getStores = () => stores.value

  /**
   * Get selected store
   */
  const getSelectedStore = () => selectedStore.value

  return {
    // Methods
    fetchUserData,
    isAuthenticated,
    clearAuth,
    
    // Getters
    getUser,
    getAbilities,
    getStores,
    getSelectedStore,
    
    // Reactive state (for components that need reactivity)
    userData,
    userAbilities,
    stores,
    selectedStore,
    isLoading,
  }
}
